import { Injectable } from '@angular/core';

import { FsApi, RequestConfig } from '@firestitch/api';

import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { WindowMessageEventType } from '@app/common/enums';
import { Account } from '@app/common/interfaces';

import { WindowMessageService } from '../services/window-message.service';


@Injectable({
  providedIn: 'root',
})
export class AccountData {

  private _recentProjectId: number;

  constructor(
    private _api: FsApi,
    private _windowMessageService: WindowMessageService,
  ) { }

  public create(data: Account = { id: null }) {
    data = { image: {}, address: {}, phone: null, ...data };
    data.fundUpdateNotification = data.fundUpdateNotification || false;
    data.fundAnomaliesEmails = data.fundAnomaliesEmails || false;

    return data;
  }

  public get(accountId, data: any = {}): Observable<any> {
    return this._api.get(`accounts/${accountId}`, data, { key: 'account' });
  }

  public gets(data = {}, config: RequestConfig = {}): Observable<any> {
    return this._api.request('GET', 'accounts', data, { key: 'accounts', ...config });
  }

  public put(account, config: RequestConfig = {}): Observable<any> {
    return this._api.put(`accounts/${account.id}`, account, { key: 'account', ...config });
  }

  public save(data): Observable<any> {
    if (data.id) {
      return this.put(data);
    }

    return this.post(data);
  }

  public post(account): Observable<any> {
    return this._api.post('accounts', account, { key: 'account' });
  }

  public delete(account): Observable<any> {
    return this._api.delete(`accounts/${account.id}`, account, { key: 'account' });
  }

  public image(account, file): Observable<any> {
    return this._api.post(`accounts/${account.id}/image`, { file }, { key: 'account' });
  }

  public putPasswordChange(account, config: RequestConfig = {}): Observable<any> {
    return this._api.put(`accounts/${account.id}/password/change`, account, { key: 'account', ...config });
  }

  public putPasswordReset(account, config: RequestConfig = {}): Observable<any> {
    return this._api.put(`accounts/${account.id}/password/reset`, account, { key: 'account', ...config });
  }

  public activate(accountId, data: any = {}): Observable<any> {
    return this._api.post(`accounts/${accountId}/activation/activate`, data, { key: 'account' });
  }

  public emailActivation(accountId, data: any = {}): Observable<any> {
    return this._api.post(`accounts/${accountId}/activation/email`, data, { key: 'account' });
  }

  public undelete(accountId, data: any = {}): Observable<any> {
    return this._api.post(`accounts/${accountId}/undelete`, data, { key: 'account' });
  }

  public getEnvironments(accountId: number, data = {}): Observable<any> {
    return this._api.request('GET', `accounts/${accountId}/environments`, data, { key: 'environments' });
  }

  public getSocialAccounts(accountId: number, data = {}): Observable<any> {
    return this._api.request('GET', `accounts/${accountId}/socialaccounts`, data, { key: 'socialAccounts' });
  }

  public postSocialAccounts(accountId: number, data = {}): Observable<any> {
    return this._api.request('POST', `accounts/${accountId}/socialaccounts`, data, { key: 'socialAccount' });
  }

  public deleteSocialAccounts(accountId: number, data = {}): Observable<any> {
    return this._api.request('DELETE', `accounts/${accountId}/socialaccounts`, data, { key: 'socialAccount' });
  }

  public notifications(data = {}, config = {}): Observable<any> {
    return this._api.request('GET', 'accounts/notifications', data, { key: 'notifications', ...config });
  }

  public notificationsRead(id, config = {}): Observable<any> {
    return this._api.request('PUT', `accounts/notifications/${id}/read`, {}, { key: 'notification', ...config });
  }

  public notificationsReadAll(config = {}): Observable<any> {
    return this._api.request('PUT', 'accounts/notifications/read/all', {}, { ...config });
  }

  public notificationsUnread(data = {}): Observable<any> {
    return this._api.request('GET', 'accounts/notifications/unread', data, {
      handlers: false,
      headers: {
        ignoreLoadingBar: '',
      },
    })
      .pipe(
        map((response: any) => (response.data.unread)),
        tap((response) => this._windowMessageService
          .broadcast(WindowMessageEventType.NotificationsUnread, response),
        ),
      );
  }

  public projectRecent(projectId): Observable<any> {
    if (this._recentProjectId === projectId) {
      return of(true);
    }

    this._recentProjectId = projectId;

    return this._api.request('POST', 'accounts/recent/projects', { projectId });
  }

}
